import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const heroSlides = [
  {
    src: 'https://readdy.ai/api/search-image?query=modern%20luxury%20office%20building%20interior%20atrium%20glass%20ceiling%20architectural%20minimalist%20elegant%20professional%20corporate%20space%20warm%20golden%20light%20marble%20floor&width=1920&height=1080&seq=hero001&orientation=landscape',
    alt: '保險建築師 專業保險規劃',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=warm%20wooden%20architecture%20interior%20modern%20meeting%20room%20soft%20natural%20light%20cozy%20professional%20environment%20minimal%20clean%20furniture%20bookshelf&width=1920&height=1080&seq=hero002&orientation=landscape',
    alt: '專業保險諮詢空間',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=modern%20cityscape%20skyline%20at%20dusk%20warm%20amber%20golden%20hour%20buildings%20silhouette%20elegant%20professional%20architecture%20panoramic%20view&width=1920&height=1080&seq=hero003&orientation=landscape',
    alt: '為人生築起保障藍圖',
  },
  {
    src: 'https://readdy.ai/api/search-image?query=elegant%20modern%20glass%20building%20facade%20reflection%20blue%20sky%20minimalist%20architecture%20professional%20corporate%20headquarters%20geometric%20lines%20clean%20design&width=1920&height=1080&seq=hero004&orientation=landscape',
    alt: '穩固保障結構',
  },
];

export default function HeroSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* Background Slideshow */}
      <div className="absolute inset-0">
        {heroSlides.map((slide, i) => (
          <div
            key={slide.src}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              i === activeIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div className={`w-full h-full transition-transform duration-[8000ms] ease-out ${
              i === activeIndex ? 'scale-105' : 'scale-100'
            }`}>
              <img
                src={slide.src}
                alt={slide.alt}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-[#0F2847]/85 via-[#0F2847]/60 to-[#0F2847]/30"></div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {heroSlides.map((_, i) => (
          <button
            key={i}
            onClick={() => setActiveIndex(i)}
            className={`transition-all duration-500 rounded-full cursor-pointer ${
              i === activeIndex
                ? 'w-8 h-2 bg-[#C9A84C]'
                : 'w-2 h-2 bg-white/40 hover:bg-white/60'
            }`}
            aria-label={`切換到第 ${i + 1} 張背景`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-10 pt-32 pb-24">
        <div className="max-w-2xl">
          {/* Brand Title */}
          <div className="mb-3 animate-fade-in">
            <span className="inline-block text-white/85 font-medium tracking-widest border-l-2 border-[#C9A84C] pl-3" style={{ fontSize: '1rem' }}>
              保險建築師｜人生風險設計師
            </span>
          </div>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-2 mb-8 animate-fade-in">
            <span className="w-2 h-2 rounded-full bg-[#C9A84C]"></span>
            <span className="text-white/90 font-medium" style={{ fontSize: '1rem' }}>專業保險規劃師 · 陳琮恩</span>
          </div>

          {/* H1 */}
          <h1
            className="font-serif font-bold text-white mb-6 animate-fade-in-up"
            style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', lineHeight: '1.3' }}
          >
            讓安心，
            <br />
            <span className="text-[#C9A84C]">成為您的人生主結構</span>
          </h1>

          {/* Subtitle */}
          <p
            className="text-white/85 mb-10 animate-fade-in-up delay-200"
            style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}
          >
            不是急著賣保單，而是先理解您想守護誰、最怕失去什麼，
            <br className="hidden sm:block" />
            再為您打造安心可以住一輩子的保障建築。
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up delay-300">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-[#C9A84C] text-white px-8 py-4 rounded-full font-semibold whitespace-nowrap hover:bg-[#b8943e] transition-all duration-200 hover:scale-105 cursor-pointer"
              style={{ fontSize: '1.1875rem' }}
            >
              <i className="ri-calendar-check-line"></i>
              免費預約諮詢
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/40 text-white px-8 py-4 rounded-full font-semibold whitespace-nowrap hover:bg-white/20 transition-all duration-200 cursor-pointer"
              style={{ fontSize: '1.1875rem' }}
            >
              了解服務項目
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="mt-20 grid grid-cols-2 lg:grid-cols-4 gap-4 animate-fade-in-up delay-400">
          {[
            { icon: 'ri-award-line', number: '15+', label: '年專業經驗' },
            { icon: 'ri-group-line', number: '500+', label: '服務客戶' },
            { icon: 'ri-shield-check-line', number: '98%', label: '客戶滿意度' },
            { icon: 'ri-file-list-3-line', number: '1000+', label: '保單規劃案' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 text-center hover:bg-white/15 transition-colors duration-200"
            >
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-3">
                <i className={`${stat.icon} text-[#C9A84C] text-2xl`}></i>
              </div>
              <div className="font-serif font-bold text-white" style={{ fontSize: '2rem', lineHeight: '1.2' }}>
                {stat.number}
              </div>
              <div className="text-white/70 mt-1" style={{ fontSize: '1rem' }}>{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-fade-in delay-500">
        <span className="text-white/50" style={{ fontSize: '1rem' }}>向下探索</span>
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-white/60 rounded-full animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}